import { useHandleCopyValue } from '@sketch/modules-common'

/**
 * The copy-on-click affordance shared by the inspector's copiable surfaces.
 *
 * `CopiableText`, `FullCopyAttribute` and `Header` all wire the same three
 * pieces together — `useHandleCopyValue`, a `CopiableArea` and a `Tooltip` —
 * and each used to spell out which handler goes on which element, with its own
 * copy of the tooltip's placement and spacing. This returns ready-made prop
 * bags for those two elements, plus the individual handlers for the components
 * that need to drive them from somewhere else (the dirty-style icon, for
 * instance, hands the tooltip back over on mouse leave).
 *
 * Pass `enabled: false` for a surface that only becomes copiable once it has a
 * value; the handlers become no-ops rather than showing an empty tooltip.
 */
export function useCopiableArea(
  value: string,
  {
    hoverTooltip,
    enabled = true,
  }: { hoverTooltip?: string; enabled?: boolean } = {}
) {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(value, hoverTooltip)

  const areaProps = {
    onClick: enabled ? handleCopyValue : undefined,
    onMouseEnter: enabled ? handleEnterCopiableArea : undefined,
    onMouseLeave: enabled ? handleLeaveCopiableArea : undefined,
  }

  const tooltipProps = {
    placement: 'left' as const,
    spacing: '10px',
    visible: copyTooltipVisible,
    content: copyTooltipText,
  }

  return {
    areaProps,
    tooltipProps,
    handleCopyValue,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipText,
    copyTooltipVisible,
  }
}
